import { useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { ChevronLeft } from "@untitledui/icons"

import { DECLINE_LABEL, type TaskAnswer, type TaskRow } from "@/lib/api/tasks"
import type { TaskKind, TaskResult } from "@/lib/tasks/task-types"
import {
  CALLBACK_PRESETS,
  callbackDate,
  canRestart,
  decisionsFor,
  hasNoteField,
  toDateInput,
  type Decision,
  type DecisionCtx,
  type Resolution,
  type Tone,
  type VisitMode,
} from "@/lib/tasks/decisions"
import { whenLabel } from "@/lib/tasks/task-copy"
import { cx } from "@/lib/utils/cx"

// The log. First the note — she types what they say while they say it —
// then one heading and rows of pills in plain words, grouped by whether she
// reached them. A pill that needs one detail opens ONE row directly under
// its group (a day, a reason, a number) and closes when she picks or goes
// back. Pills are choices; fields are fields; the two never share a shape.

const EASE = [0.23, 1, 0.32, 1] as const

const TONE: Record<Tone, string> = {
  do: "bg-zinc-900 border-zinc-900 text-white hover:bg-black",
  log: "bg-white border-zinc-300 text-zinc-800 hover:border-zinc-400 hover:bg-zinc-50",
  close: "bg-white border-red-200 text-red-700 hover:border-red-300 hover:bg-red-50",
}

export function Pill({
  tone,
  label,
  open,
  disabled,
  disabledWhy,
  onClick,
  className,
}: {
  tone: Tone
  label: string
  open?: boolean
  disabled?: boolean
  disabledWhy?: string | null
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || !!disabledWhy}
      title={disabledWhy ?? undefined}
      aria-expanded={open}
      className={cx(
        "h-11 px-4 rounded-full border text-[14px] font-medium inline-flex items-center gap-1.5 whitespace-nowrap",
        "transition-[transform,background-color,border-color,color] duration-150 ease-out-quart active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/20 focus-visible:ring-offset-1",
        "disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100",
        TONE[tone],
        open && "ring-2 ring-zinc-900/15 ring-offset-1 border-zinc-900",
        className,
      )}
    >
      {label}
    </button>
  )
}

/** A quiet, secondary action — the same height as a pill, none of the weight. */
export function Quiet({ label, active, disabled, onClick }: { label: string; active?: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cx(
        "h-10 px-2.5 rounded-lg text-[13px] font-medium transition-colors duration-150 ease-out-quart motion-reduce:transition-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/20 focus-visible:ring-offset-1 disabled:opacity-40",
        active ? "bg-zinc-100 text-zinc-900" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900",
      )}
    >
      {label}
    </button>
  )
}

export const FIELD =
  "h-11 px-3.5 rounded-lg border border-zinc-300 bg-white text-[14px] text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10 transition-[border-color,box-shadow] duration-150 ease-out-quart motion-reduce:transition-none"

/** The one open row under a pill group. Height is tolerated here: it's an
 *  accordion, and there is no transform equivalent. Reduced motion keeps the
 *  fade and drops the movement. */
function Row({ title, onBack, children }: { title: string; onBack: () => void; children: React.ReactNode }) {
  const reduce = useReducedMotion()
  return (
    <motion.div
      key="row"
      initial={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
      animate={reduce ? { opacity: 1 } : { height: "auto", opacity: 1 }}
      exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
      transition={{ duration: reduce ? 0.12 : 0.18, ease: EASE }}
      className="overflow-hidden"
    >
      <div className="pt-2.5">
      <div className="rounded-lg bg-zinc-50 border border-zinc-200/80 p-3.5">
        <div className="flex items-center justify-between gap-3 mb-2.5">
          <p className="text-[13px] font-semibold text-zinc-900">{title}</p>
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-0.5 h-8 px-1.5 -mr-1.5 rounded-md text-[13px] font-medium text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/20"
          >
            <ChevronLeft className="size-3.5" aria-hidden /> Back
          </button>
        </div>
        {children}
      </div>
      </div>
    </motion.div>
  )
}

export interface DecisionsProps {
  kind: TaskKind
  row: TaskRow
  mode: VisitMode
  ctx: DecisionCtx
  busy: boolean
  note: string
  onNote: (v: string) => void
  restart: boolean
  onRestart: (v: boolean) => void
  onPost: (answer: TaskAnswer, opts?: { after?: () => Promise<void>; startedTexts?: boolean }) => void
  onDone: (result: TaskResult) => void
  onMode: (mode: VisitMode) => void
  /** Wrong number: with a number, three writes in one click; without, the fix task. */
  onWrongNumber: (newPhone: string | null) => void
}

export function Decisions({ kind, row, mode, ctx, busy, note, onNote, restart, onRestart, onPost, onDone, onMode, onWrongNumber }: DecisionsProps) {
  const [open, setOpen] = useState<string | null>(null)
  const [pickDate, setPickDate] = useState("")
  const [newPhone, setNewPhone] = useState("")
  const d = decisionsFor(kind, mode)
  const first = row.customerName.split(" ")[0] || row.customerName
  const persisted = row.id > 0
  const close = () => setOpen(null)

  const resolve = async (r: Resolution) => {
    if (!r) return
    if ("post" in r) onPost(r.post, { after: r.after ? () => r.after!(ctx) : undefined, startedTexts: r.startedTexts })
    else if ("done" in r) onDone(r.done)
    else if ("mode" in r) {
      close()
      onMode(r.mode)
    }
  }
  const choose = async (c: Decision) => {
    if (c.opens) {
      setOpen((o) => (o === c.id ? null : c.id))
      return
    }
    close()
    if (c.post) onPost(c.post)
    else if (c.run) await resolve(await c.run(ctx))
  }

  const noteLabel = kind === "call" || kind === "wrapup" ? `What did ${first} say?` : kind === "reply" ? "A note for the file" : "Anything to note?"
  const withNote = hasNoteField(kind)
  const noteInput = (className: string, placeholder: string) => (
    <input
      value={note}
      onChange={(e) => onNote(e.target.value)}
      maxLength={160}
      placeholder={placeholder}
      aria-label="Note"
      className={cx(FIELD, className)}
    />
  )

  const openRow = (c: Decision) => {
    switch (c.opens) {
      case "days":
        return (
          <Row key={c.id} title="Which day?" onBack={close}>
            <div className="flex flex-wrap items-center gap-2">
              {CALLBACK_PRESETS.map((p) => (
                <Pill key={p.label} tone="log" label={p.label} disabled={busy} onClick={() => onPost(c.callback!(callbackDate(p.days).toISOString()))} />
              ))}
              <input
                type="date"
                value={pickDate}
                min={toDateInput(new Date())}
                onChange={(e) => setPickDate(e.target.value)}
                aria-label="Pick a day"
                className={cx(FIELD, "w-44")}
              />
              {pickDate && (
                <Pill tone="do" label={`Set ${whenLabel(`${pickDate}T09:00:00`, new Date(), false)}`} disabled={busy} onClick={() => onPost(c.callback!(new Date(`${pickDate}T09:00:00`).toISOString()))} />
              )}
            </div>
            <p className="mt-2.5 text-[13px] text-zinc-500">The texts wait until then.</p>
          </Row>
        )
      case "reasons":
      case "stop":
        return (
          <Row key={c.id} title={c.opens === "stop" ? "Why stop them?" : `Why did ${first} pass?`} onBack={close}>
            <div className="flex flex-wrap gap-2">
              {c.opens === "stop" && (
                <Pill
                  tone="log"
                  label="I'll handle it by phone"
                  disabled={busy}
                  onClick={async () => {
                    await ctx.api.stopTexts(row.customerId!)
                    onDone({ kind: "texts_stopped" })
                  }}
                />
              )}
              {c.reasons!.list.map((r) => (
                <Pill
                  key={r}
                  tone="close"
                  label={c.opens === "stop" ? `${first} isn't interested — ${DECLINE_LABEL[r].toLowerCase()}` : DECLINE_LABEL[r]}
                  disabled={busy}
                  disabledWhy={r === "other" && !note.trim() ? "Write a line in the note first" : null}
                  onClick={async () => resolve(await c.reasons!.resolve(r, ctx))}
                />
              ))}
            </div>
            {!withNote && <div className="mt-2.5">{noteInput("w-full", "A line about why (needed for Other)")}</div>}
          </Row>
        )
      case "number":
        return (
          <Row key={c.id} title="Got a new number?" onBack={close}>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="tel"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder="(555) 123-4567"
                aria-label={`${first}'s new phone number`}
                className={cx(FIELD, "w-48")}
              />
              <Pill
                tone="do"
                label="Save and call again"
                disabled={busy}
                disabledWhy={newPhone.replace(/\D/g, "").length >= 10 ? null : "Type the number first"}
                onClick={() => onWrongNumber(newPhone.trim())}
              />
              <Pill tone="log" label="Don't have one" disabled={busy} onClick={() => onWrongNumber(null)} />
            </div>
          </Row>
        )
      default:
        return null
    }
  }

  return (
    <div>
      {/* The note first — before the pills, because it travels with the pick. */}
      {withNote && (
        <label className="block">
          <span className="block text-[13px] font-medium text-zinc-600 mb-1.5">{noteLabel}</span>
          {noteInput("w-full", kind === "call" || kind === "wrapup" ? "Wants to wait until payday" : "Optional")}
        </label>
      )}

      <div className={cx("flex items-center justify-between gap-3 flex-wrap", withNote && "mt-5")}>
        <h3 className="text-[15px] font-semibold text-zinc-900">{d.heading(row)}</h3>
        {persisted && (
          <div className="flex items-center gap-0.5 -mr-2.5">
            <Quiet
              label="Not today"
              disabled={busy}
              onClick={async () => {
                const t = await ctx.api.skip()
                onDone({ kind: "skipped", dueAt: t.dueAt })
              }}
            />
            <Quiet label="Pick a day" active={open === "__day"} disabled={busy} onClick={() => setOpen((o) => (o === "__day" ? null : "__day"))} />
          </div>
        )}
      </div>
      <AnimatePresence initial={false}>
        {open === "__day" && (
          <Row key="__day" title="Move it to which day?" onBack={close}>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="date"
                value={pickDate}
                min={toDateInput(new Date())}
                onChange={(e) => setPickDate(e.target.value)}
                aria-label="Move it to this day"
                className={cx(FIELD, "w-44")}
              />
              <Pill
                tone="do"
                label={pickDate ? `Move to ${whenLabel(`${pickDate}T09:00:00`, new Date(), false)}` : "Move"}
                disabled={!pickDate || busy}
                onClick={async () => {
                  const until = new Date(`${pickDate}T09:00:00`).toISOString()
                  await ctx.api.snooze(until, note.trim() || null)
                  onDone({ kind: "snoozed", until })
                }}
              />
            </div>
            <p className="mt-2.5 text-[13px] text-zinc-500">Nothing is logged, and the texts wait until then.</p>
          </Row>
        )}
      </AnimatePresence>

      {d.groups.map((g, i) => {
        const chips = g.chips.filter((c) => !c.when || c.when(row))
        if (chips.length === 0) return null
        const opened = chips.find((c) => c.id === open)
        return (
          <div key={i} className={i === 0 ? "mt-3" : "mt-4"}>
            {g.label && <p className="text-[12.5px] font-medium text-zinc-500 mb-2">{g.label(row)}</p>}
            <div className="flex flex-wrap gap-2">
              {chips.map((c) => (
                <Pill
                  key={c.id}
                  tone={c.tone}
                  label={c.label}
                  open={open === c.id}
                  disabled={busy}
                  disabledWhy={c.disabledWhy?.(ctx) ?? null}
                  onClick={() => void choose(c)}
                />
              ))}
            </div>
            <AnimatePresence initial={false}>{opened && openRow(opened)}</AnimatePresence>
            {i === 0 && kind === "call" && canRestart(row) && (
              <label className="mt-2.5 flex items-center gap-2 text-[13px] text-zinc-600">
                <input type="checkbox" checked={restart} onChange={(e) => onRestart(e.target.checked)} className="size-4 rounded border-zinc-300" />
                Start the texts again from the top (they've run out)
              </label>
            )}
          </div>
        )
      })}
      {d.hint && <p className="mt-3.5 text-[13px] text-zinc-500">{d.hint(row)}</p>}
    </div>
  )
}
